import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { ArrowRight, ShieldCheck, Mail, Phone, Lock, Sparkles, Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";

// Re-enabling this requires a reviewed backend SMS integration, not an environment toggle.
const PHONE_AUTH_ENABLED = false;

export default function Login() {
  const { login, loginWithGoogle, refreshUser, isAuthProcessing } = useAuth();
  const { register, handleSubmit, formState: { isSubmitting } } = useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || location.state?.from || "/dashboard";

  const [screen, setScreen] = useState("login"); // "login" | "verify" | "link-phone" | "forgot" | "reset"
  const [loginMode, setLoginMode] = useState("email"); // "email" | "phone" | "google"
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);

  // OTP Verification details
  const [verificationTarget, setVerificationTarget] = useState({ type: "email", value: "" }); // type: "email" | "phone"
  const [otpCode, setOtpCode] = useState("");

  // Google Login linking details
  const [linkPhoneVal, setLinkPhoneVal] = useState("");
  const [linkOtpCode, setLinkOtpCode] = useState("");
  const [linkOtpSent, setLinkOtpSent] = useState(false);
  const [tempUser, setTempUser] = useState(null);

  // Password reset details
  const [resetMode, setResetMode] = useState("email"); // "email" | "phone"
  const [resetEmail, setResetEmail] = useState("");
  const [resetPhone, setResetPhone] = useState("");
  const [resetOtpCode, setResetOtpCode] = useState("");
  const [newPassword, setNewPassword] = useState("");

  // Cooldown countdown timer effect
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  // Submit handler for Email and Phone login
  const onSubmit = async (values) => {
    setBusy(true);
    try {
      const loginIdentifier = loginMode === "email" ? values.email : values.phone;
      const res = await login(loginIdentifier, values.password);
      if (res.ok) {
        const userObj = res.user;
        if (loginMode === "email" && !userObj.email_verified) {
          setVerificationTarget({ type: "email", value: userObj.email });
          setScreen("verify");
          await api.post("/auth/otp/send", { email: userObj.email });
          setCooldown(60);
          toast.info("Verification OTP sent to your email!");
        } else if (loginMode === "phone" && !userObj.phone_verified) {
          setVerificationTarget({ type: "phone", value: userObj.phone });
          setScreen("verify");
          await api.post("/auth/otp/send", { phone: userObj.phone });
          setCooldown(60);
          toast.info("Verification OTP sent to your phone!");
        } else {
          toast.success("Welcome back!");
          navigate(from, { replace: true, state: { scrollY: location.state?.scrollY } });
        }
      } else {
        toast.error(res.error || "Login failed");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  // Resend OTP handler for login flow
  const handleResendOtp = async () => {
    if (cooldown > 0) return;
    setBusy(true);
    try {
      const otpPayload = verificationTarget.type === "email" 
        ? { email: verificationTarget.value } 
        : { phone: verificationTarget.value };
      await api.post("/auth/otp/send", otpPayload);
      setCooldown(60);
      toast.success("OTP verification code resent successfully!");
    } catch (err) {
      toast.error(formatApiError(err) || "Failed to resend OTP");
    } finally {
      setBusy(false);
    }
  };

  // OTP Verification Submission
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otpCode.length !== 6) {
      toast.error("Please enter the 6-digit OTP code");
      return;
    }
    setBusy(true);
    try {
      const payload = { otp: otpCode };
      if (verificationTarget.type === "email") {
        payload.email = verificationTarget.value;
      } else {
        payload.phone = verificationTarget.value;
      }
      await api.post("/auth/verify-otp", payload);
      toast.success("Verification successful!");
      await refreshUser();
      navigate(from, { replace: true, state: { scrollY: location.state?.scrollY } });
    } catch (err) {
      toast.error(formatApiError(err) || "OTP verification failed");
    } finally {
      setBusy(false);
    }
  };

  // Google OAuth Initialization
  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const targetId = loginMode === "email" ? "google-signin-div-email" : "google-signin-div-phone";
    const target = document.getElementById(targetId);
    if (!clientId || !target) return;

    const existing = document.querySelector('script[data-google-identity="true"]');
    const render = () => {
      if (!window.google || !target) return;
      target.innerHTML = "";
      window.google.accounts.id.initialize({ client_id: clientId, callback: handleGoogleCredential });
      window.google.accounts.id.renderButton(target, { theme: "outline", size: "large", width: Math.min(384, target.clientWidth || 384) });
    };

    if (existing) { render(); return; }
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.dataset.googleIdentity = "true";
    script.onload = render;
    document.body.appendChild(script);
  }, [loginMode]);

  // Google credential handling
  const handleGoogleCredential = async (response) => {
    setBusy(true);
    try {
      const result = await loginWithGoogle(response.credential);
      if (!result.ok) {
        toast.error(result.error || "Google sign-in failed");
        return;
      }

      if (PHONE_AUTH_ENABLED && !result.user.phone) {
        setTempUser(result.user);
        setScreen("link-phone");
      } else {
        toast.success("Successfully signed in with Google!");
        navigate(from, { replace: true, state: { scrollY: location.state?.scrollY } });
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  // Google flow: Send Linking Phone OTP
  const handleSendLinkPhoneOtp = async (e) => {
    if (e) e.preventDefault();
    if (linkPhoneVal.length < 8) {
      toast.error("Please enter a valid phone number");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/otp/send", { phone: linkPhoneVal });
      setLinkOtpSent(true);
      setCooldown(60);
      toast.success("OTP sent to phone!");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  // Google flow: Verify Linking Phone OTP
  const handleVerifyLinkPhoneOtp = async (e) => {
    e.preventDefault();
    if (linkOtpCode.length !== 6) {
      toast.error("Please enter a 6-digit OTP code");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/verify-otp", {
        user_id: tempUser.id || tempUser.user_id,
        phone: linkPhoneVal,
        otp: linkOtpCode
      });
      toast.success("Phone verified and linked successfully!");
      await refreshUser();
      navigate(from, { replace: true, state: { scrollY: location.state?.scrollY } });
    } catch (err) {
      toast.error(formatApiError(err) || "OTP verification failed");
    } finally {
      setBusy(false);
    }
  };

  // Google flow: Skip Phone Linking
  const handleSkipPhoneLink = () => {
    toast.success("Welcome back!");
    navigate(from, { replace: true, state: { scrollY: location.state?.scrollY } });
  };

  // Password reset handlers
  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    const targetVal = resetMode === "email" ? resetEmail : resetPhone;
    if (!targetVal) {
      toast.error(`Please enter a valid ${resetMode}`);
      return;
    }
    setBusy(true);
    try {
      const payload = resetMode === "email" ? { email: resetEmail } : { phone: resetPhone };
      await api.post("/auth/forgot-password", payload);
      setCooldown(60);
      toast.success(`Reset OTP code sent to your ${resetMode}!`);
      setScreen("reset");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  // Resend OTP for Forgot Password Flow
  const handleResendResetOtp = async () => {
    if (cooldown > 0) return;
    setBusy(true);
    try {
      const payload = resetMode === "email" ? { email: resetEmail } : { phone: resetPhone };
      await api.post("/auth/forgot-password", payload);
      setCooldown(60);
      toast.success("Reset OTP verification code resent!");
    } catch (err) {
      toast.error(formatApiError(err) || "Failed to resend reset OTP");
    } finally {
      setBusy(false);
    }
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    if (!resetOtpCode || !newPassword) return;
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters long");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        otp: resetOtpCode,
        password: newPassword
      };
      if (resetMode === "email") {
        payload.email = resetEmail;
      } else {
        payload.phone = resetPhone;
      }
      await api.post("/auth/reset-password", payload);
      toast.success("Password reset successfully! You can now log in.");
      setScreen("login");
      setResetOtpCode("");
      setNewPassword("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="site-shell min-h-screen grid lg:grid-cols-2 bg-background w-full overflow-y-auto" data-testid="login-page">
      {/* Left side: Premium Branding */}
      <div className="premium-dark-panel hidden lg:flex flex-col justify-between bg-primary text-primary-foreground p-16 relative overflow-hidden h-full min-h-screen">
        <Link to="/" className="mb-12 relative z-10 inline-flex" aria-label="ShreeHomes home">
          {/* Supplied logo anchors the desktop authentication experience. */}
          <BrandLogo inverse size="md" subtitle="The real place for real estate" />
        </Link>
        <div className="my-auto relative z-10 max-w-md">
          <h2 className="font-display text-5xl font-black tracking-tighter leading-[1.05]">
            Welcome back. <em className="font-light">Your next home is one search away.</em>
          </h2>
          <p className="text-primary-foreground/75 mt-6 text-base leading-relaxed">Sign in to track saved properties, manage appointments, and pick up where you left off.</p>
        </div>
        <div className="text-xs text-primary-foreground/45 mt-auto relative z-10">
          &copy; {new Date().getFullYear()} ShreeHomes. All rights reserved.
        </div>
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-primary-foreground/5 rounded-full animate-float-soft"/>
        <div className="absolute -bottom-16 -right-16 w-64 h-64 bg-primary-foreground/5 rounded-full animate-float-soft" style={{ animationDelay: "1.5s" }}/>
      </div>

      {/* Right side: Login Panel */}
      <div className="flex items-center justify-center p-6 sm:p-12 lg:p-16 w-full my-auto">
        <div className="premium-card w-full max-w-lg bg-card border border-border/60 rounded-3xl p-8 sm:p-10">
          <Link to="/" className="mb-8 inline-flex lg:hidden" aria-label="ShreeHomes home">
            <BrandLogo size="sm" subtitle="The real place for real estate" />
          </Link>
          <p className="gold-eyebrow text-xs font-bold uppercase tracking-[0.25em] mb-3">Sign in</p>

          {screen === "login" && (
            <>
              <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-primary">Login to your account</h1>
              
              {/* Tab Selector */}
              <div className="mt-6 flex border-b border-border" data-testid="login-mode-toggle">
                {(PHONE_AUTH_ENABLED ? ["email", "phone"] : ["email"]).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setLoginMode(mode)}
                    className={`flex-1 pb-3 text-sm font-semibold capitalize transition-all border-b-2 ${
                      loginMode === mode
                        ? "text-primary border-primary font-bold"
                        : "text-muted-foreground border-transparent hover:text-foreground"
                    }`}
                    data-testid={`login-mode-${mode}`}
                  >
                    {mode === "email" ? "Email & Password" : "Phone & Password"}
                  </button>
                ))}
              </div>

              {loginMode === "email" && (
                <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
                  <div className="space-y-1.5">
                    <Label className="flex items-center gap-1.5"><Mail className="w-4 h-4 text-muted-foreground"/>Email</Label>
                    <Input type="email" {...register("email", { required: true })} data-testid="login-email" placeholder="you@example.com" />
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex justify-between items-center">
                      <Label className="flex items-center gap-1.5"><Lock className="w-4 h-4 text-muted-foreground"/>Password</Label>
                      <Button type="button" variant="link" onClick={() => { setResetMode("email"); setScreen("forgot"); }} className="text-xs text-muted-foreground p-0 h-auto font-semibold hover:text-primary">Forgot Password?</Button>
                    </div>
                    <div className="relative">
                      <Input type={showPassword ? "text" : "password"} {...register("password", { required: true })} data-testid="login-password" placeholder="••••••••" className="pr-10" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" disabled={busy || isSubmitting || isAuthProcessing} className="w-full font-bold gap-2 mt-2" data-testid="login-submit">
                    {busy || isSubmitting || isAuthProcessing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin text-white" />
                        <span>Signing in…</span>
                      </>
                    ) : (
                      <>
                        <span>Sign in</span>
                        <ArrowRight className="w-4 h-4"/>
                      </>
                    )}
                  </Button>

                  <div className="relative flex py-4 items-center">
                    <div className="flex-grow border-t border-border"></div>
                    <span className="flex-shrink mx-4 text-muted-foreground text-xs font-semibold uppercase tracking-wider">Or continue with</span>
                    <div className="flex-grow border-t border-border"></div>
                  </div>

                  {process.env.REACT_APP_GOOGLE_CLIENT_ID ? (
                    <div id="google-signin-div-email" className="w-full flex justify-center py-2" data-testid="google-login-button" />
                  ) : (
                    <div className="rounded-xl border border-dashed border-border bg-secondary/50 p-3 text-center text-xs text-muted-foreground" data-testid="google-login-disabled">
                      Google sign-in is disabled until REACT_APP_GOOGLE_CLIENT_ID is configured.
                    </div>
                  )}
                </form>
              )}

              {loginMode === "phone" && (
                <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
                  <div className="space-y-1.5">
                    <Label className="flex items-center gap-1.5"><Phone className="w-4 h-4 text-muted-foreground"/>Phone Number</Label>
                    <Input type="tel" {...register("phone", { required: true })} placeholder="+919999999999" data-testid="phone-login-number" />
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex justify-between items-center">
                      <Label className="flex items-center gap-1.5"><Lock className="w-4 h-4 text-muted-foreground"/>Password</Label>
                      <Button type="button" variant="link" onClick={() => { setResetMode("phone"); setScreen("forgot"); }} className="text-xs text-muted-foreground p-0 h-auto font-semibold hover:text-primary">Forgot Password?</Button>
                    </div>
                    <div className="relative">
                      <Input type={showPassword ? "text" : "password"} {...register("password", { required: true })} placeholder="••••••••" data-testid="phone-login-password" className="pr-10" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" disabled={busy || isSubmitting || isAuthProcessing} className="w-full font-bold gap-2 mt-2" data-testid="phone-login-submit">
                    {busy || isSubmitting || isAuthProcessing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin text-white" />
                        <span>Signing in…</span>
                      </>
                    ) : (
                      <>
                        <span>Sign in</span>
                        <ArrowRight className="w-4 h-4"/>
                      </>
                    )}
                  </Button>

                  <div className="relative flex py-4 items-center">
                    <div className="flex-grow border-t border-border"></div>
                    <span className="flex-shrink mx-4 text-muted-foreground text-xs font-semibold uppercase tracking-wider">Or continue with</span>
                    <div className="flex-grow border-t border-border"></div>
                  </div>

                  {process.env.REACT_APP_GOOGLE_CLIENT_ID ? (
                    <div id="google-signin-div-phone" className="w-full flex justify-center py-2" data-testid="google-login-button" />
                  ) : (
                    <div className="rounded-xl border border-dashed border-border bg-secondary/50 p-3 text-center text-xs text-muted-foreground" data-testid="google-login-disabled">
                      Google sign-in is disabled until REACT_APP_GOOGLE_CLIENT_ID is configured.
                    </div>
                  )}
                </form>
              )}
            </>
          )}

          {/* Screen: One-time Verification for unverified email/phone */}
          {screen === "verify" && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <ShieldCheck className="w-10 h-10 text-primary shrink-0" />
                <div>
                  <h1 className="font-display text-2xl font-black tracking-tighter text-primary">Verify your {verificationTarget.type}</h1>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Enter the code sent to <strong className="text-foreground">{verificationTarget.value}</strong>
                  </p>
                </div>
              </div>
              <form onSubmit={handleVerifyOtp} className="space-y-4 pt-2">
                <div className="space-y-1.5">
                  <Label>Verification Code</Label>
                  <Input
                    type="text"
                    maxLength={6}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="123456"
                    className="text-center font-bold tracking-[0.5em] text-xl h-12 bg-muted/30 focus-visible:bg-background"
                    data-testid="verify-otp-input"
                    required
                  />
                </div>
                <div className="flex flex-col sm:flex-row gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={busy || cooldown > 0}
                    onClick={handleResendOtp}
                    className="flex-1 font-semibold"
                  >
                    {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend Code"}
                  </Button>
                  <Button type="submit" disabled={busy} className="flex-1 font-bold">
                    {busy ? "Verifying…" : "Verify OTP Code"}
                  </Button>
                </div>
                <Button type="button" variant="link" onClick={() => setScreen("login")} className="w-full text-xs text-muted-foreground">
                  ← Back to Login
                </Button>
              </form>
            </div>
          )}

          {/* Screen: Google Sign-in Skippable Phone Link */}
          {screen === "link-phone" && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Sparkles className="w-10 h-10 text-primary shrink-0 animate-pulse" />
                <div>
                  <h1 className="font-display text-2xl font-black tracking-tighter text-primary">Link Phone Number</h1>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Receive important listing and verification updates directly on your phone.
                  </p>
                </div>
              </div>
              <form onSubmit={linkOtpSent ? handleVerifyLinkPhoneOtp : handleSendLinkPhoneOtp} className="space-y-4 pt-2">
                {!linkOtpSent ? (
                  <div className="space-y-1.5">
                    <Label>Mobile Number</Label>
                    <Input
                      type="tel"
                      value={linkPhoneVal}
                      onChange={(e) => setLinkPhoneVal(e.target.value)}
                      placeholder="+919999999999"
                      required
                    />
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <Label>6-Digit Verification Code</Label>
                    <Input
                      type="text"
                      maxLength={6}
                      value={linkOtpCode}
                      onChange={(e) => setLinkOtpCode(e.target.value.replace(/\D/g, ""))}
                      placeholder="123456"
                      className="text-center font-bold tracking-[0.5em] text-xl h-12 bg-muted/30 focus-visible:bg-background"
                      required
                    />
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={handleSkipPhoneLink} className="flex-1 font-semibold">
                    Skip for now
                  </Button>
                  <Button type="submit" disabled={busy} className="flex-1 font-bold">
                    {busy ? "Processing…" : (linkOtpSent ? "Verify & Link" : "Send OTP")}
                  </Button>
                </div>
                {linkOtpSent && (
                  <div className="flex flex-col items-center gap-1">
                    <Button type="button" variant="link" disabled={busy || cooldown > 0} onClick={handleSendLinkPhoneOtp} className="text-xs text-muted-foreground p-0 h-auto">
                      {cooldown > 0 ? `Resend Code in ${cooldown}s` : "Resend Verification Code"}
                    </Button>
                    <Button type="button" variant="link" onClick={() => setLinkOtpSent(false)} className="text-xs text-muted-foreground p-0 h-auto">
                      ← Change phone number
                    </Button>
                  </div>
                )}
              </form>
            </div>
          )}

          {/* Forgot Password screen */}
          {screen === "forgot" && (
            <form onSubmit={handleForgotPasswordSubmit} className="mt-8 space-y-4">
              <h1 className="font-display text-3xl font-black tracking-tighter text-primary">Reset password</h1>
              <p className="text-sm text-muted-foreground">Select how you want to reset your password and enter your details.</p>
              
              <div className="flex border-b border-border mb-2">
                {(PHONE_AUTH_ENABLED ? ["email", "phone"] : ["email"]).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => {
                      setResetMode(mode);
                      setResetEmail("");
                      setResetPhone("");
                    }}
                    className={`flex-1 pb-2 text-xs font-semibold capitalize border-b-2 ${
                      resetMode === mode
                        ? "text-primary border-primary font-bold"
                        : "text-muted-foreground border-transparent hover:text-foreground"
                    }`}
                  >
                    Reset via {mode === "email" ? "Email" : "Phone"}
                  </button>
                ))}
              </div>

              {resetMode === "email" ? (
                <div className="space-y-1.5">
                  <Label>Email</Label>
                  <Input type="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} required placeholder="you@example.com" />
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label>Phone Number</Label>
                  <Input type="tel" value={resetPhone} onChange={(e) => setResetPhone(e.target.value)} required placeholder="+919999999999" />
                </div>
              )}

              <Button type="submit" disabled={busy} className="w-full font-bold">
                {busy ? "Sending OTP…" : "Send Reset OTP"}
              </Button>
              <Button type="button" variant="link" onClick={() => setScreen("login")} className="w-full text-xs text-muted-foreground">
                ← Back to Login
              </Button>
            </form>
          )}

          {/* Reset Password screen */}
          {screen === "reset" && (
            <form onSubmit={handleResetPasswordSubmit} className="mt-8 space-y-4">
              <h1 className="font-display text-3xl font-black tracking-tighter text-primary">Enter verification details</h1>
              <p className="text-sm text-muted-foreground">Enter the 6-digit code sent to your {resetMode} and your new password.</p>
              <div className="space-y-1.5">
                <Label>OTP Code</Label>
                <Input type="text" maxLength={6} value={resetOtpCode} onChange={(e) => setResetOtpCode(e.target.value.replace(/\D/g, ""))} required placeholder="123456" className="text-center font-bold text-xl h-12 tracking-[0.5em] bg-muted/30 focus-visible:bg-background" />
              </div>
              <div className="space-y-1.5">
                <Label>New Password</Label>
                <div className="relative">
                  <Input type={showResetPassword ? "text" : "password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required placeholder="••••••••" className="pr-10" />
                  <button type="button" onClick={() => setShowResetPassword(!showResetPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {showResetPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={busy || cooldown > 0}
                  onClick={handleResendResetOtp}
                  className="flex-1 font-semibold"
                >
                  {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend Code"}
                </Button>
                <Button type="submit" disabled={busy} className="flex-1 font-bold">
                  {busy ? "Resetting…" : "Reset Password"}
                </Button>
              </div>
              <Button type="button" variant="link" onClick={() => setScreen("forgot")} className="w-full text-xs text-muted-foreground">
                ← Resend OTP / Go back
              </Button>
            </form>
          )}

          <p className="mt-8 text-sm text-muted-foreground text-center">
            New to ShreeHomes? <Link to="/register" state={{ from, scrollY: location.state?.scrollY }} className="text-primary font-bold hover:underline" data-testid="register-cta">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
